import type { ConstValues } from '@openx/types/helpers.ts';
import type { AudiencePricing } from '@openx/types/targeting/audience.ts';
import type { ContextualPricingApi } from '@openx/types/targeting/contextualPricing.ts';
import { toNumber } from '@openx/utils/lib/toNumber.ts';

export const PartnerType = {
  AUDIENCE: 'audience',
  CONTEXTUAL: 'contextual',
} as const;
export type PartnerType = ConstValues<typeof PartnerType>;

export type PartnerFeeRow = {
  partnerId: string;
  partnerType: PartnerType;
  /** Set for contextual partners, when the app could name them. */
  partnerName?: string;
  priceCpm: number;
  pricePom: number;
};

const toRows = (
  pricingByOrg:
    | Record<string, { price_cpm: string | number; price_pom: string | number; name?: string }>
    | null
    | undefined,
  partnerType: PartnerType
): PartnerFeeRow[] =>
  Object.entries(pricingByOrg ?? {}).map(([partnerId, { price_cpm, price_pom, name }]) => ({
    partnerId,
    partnerType,
    ...(name ? { partnerName: name } : {}),
    priceCpm: toNumber(price_cpm),
    pricePom: toNumber(price_pom),
  }));

/** Audience partners first, then contextual ones; a partner present in both is listed once per type. */
export const buildPartnerFeeRows = (
  audiencePricing?: Pick<AudiencePricing, 'pricing_by_org'> | null,
  contextualPricing?: Pick<ContextualPricingApi, 'pricing_by_org'> | null
): PartnerFeeRow[] => [
  ...toRows(audiencePricing?.pricing_by_org, PartnerType.AUDIENCE),
  ...toRows(contextualPricing?.pricing_by_org, PartnerType.CONTEXTUAL),
];

/** Partners in first-seen order; the platform fee is stored once per partner, whatever its types. */
export const getUniquePartnerIds = (rows: PartnerFeeRow[]): string[] => [
  ...new Set(rows.map(({ partnerId }) => partnerId)),
];

/** Audience rows always carry the OpenX platform fee; contextual ones only once the API can store it. */
export const hasPlatformFee = ({ partnerType }: Pick<PartnerFeeRow, 'partnerType'>, isContextualSupported: boolean) =>
  partnerType === PartnerType.AUDIENCE || isContextualSupported;

/** "Partner Type" captions are shown only when both types are present. */
export const hasBothPartnerTypes = (rows: PartnerFeeRow[]): boolean =>
  rows.some(({ partnerType }) => partnerType === PartnerType.AUDIENCE) &&
  rows.some(({ partnerType }) => partnerType === PartnerType.CONTEXTUAL);

/**
 * What a partner row is headed with. An audience partner is an organization, named by its record. A contextual
 * partner is named after its provider, as the Data Partner filter shows it, and only falls back to an organization
 * with that id. Without any name the id is shown.
 */
export const getPartnerDisplayName = (
  { partnerId, partnerType, partnerName }: Pick<PartnerFeeRow, 'partnerId' | 'partnerType' | 'partnerName'>,
  organizationName?: string | null
): string => {
  if (partnerType === PartnerType.CONTEXTUAL) return partnerName || organizationName || partnerId;

  return organizationName || partnerId;
};
